// src/components/site-editor/panels/AboutPanel.js
import React, { useState, useEffect } from 'react';
import { ArrowRight, Sparkles, PenLine, CheckCircle } from 'lucide-react';
import InlineCallout from '../InlineCallout';
import SimpleRichEditor from '../../common/SimpleRichEditor';
import Toggle from '../../Toggle';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { useNavigate } from 'react-router-dom';

const CHARACTER_LIMIT = 1500;
const AMBER_THRESHOLD = 1350;

// ── Photo Controls (module scope per bug pattern #3) ──

const radioStyle = (selected) => ({
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '6px 12px', borderRadius: 6, fontSize: 13,
  fontFamily: "'Montserrat', sans-serif", cursor: 'pointer',
  background: selected ? '#F5F3FF' : '#F9FAFB',
  border: `1px solid ${selected ? '#7c3aed' : '#E5E7EB'}`,
  color: selected ? '#7c3aed' : '#374151', fontWeight: selected ? 600 : 400,
  transition: 'all 0.15s',
});

const PhotoControls = ({ photoSettings, onUpdate, onNavigateProfile }) => {
  const s = photoSettings || { show: false, shape: 'circle', size: 'medium', position: 'right' };

  return (
    <div style={{
      border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px', background: '#F9FAFB',
      }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>Show profile photo</span>
        <Toggle enabled={s.show} onChange={() => onUpdate({ ...s, show: !s.show })} size="small" />
      </div>

      {s.show && (
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Shape */}
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Shape</p>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['circle', 'Circle'], ['rounded', 'Rounded'], ['square', 'Square']].map(([val, label]) => (
                <span key={val} role="button" tabIndex={0} style={radioStyle(s.shape === val)}
                  onClick={() => onUpdate({ ...s, shape: val })}
                  onKeyDown={(e) => e.key === 'Enter' && onUpdate({ ...s, shape: val })}
                >{label}</span>
              ))}
            </div>
          </div>

          {/* Size */}
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Size</p>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['small', 'Small'], ['medium', 'Medium'], ['large', 'Large']].map(([val, label]) => (
                <span key={val} role="button" tabIndex={0} style={radioStyle(s.size === val)}
                  onClick={() => onUpdate({ ...s, size: val })}
                  onKeyDown={(e) => e.key === 'Enter' && onUpdate({ ...s, size: val })}
                >{label}</span>
              ))}
            </div>
          </div>

          {/* Position */}
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Layout</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[['left', 'Photo left'], ['right', 'Photo right'], ['above', 'Above text']].map(([val, label]) => (
                <span key={val} role="button" tabIndex={0} style={radioStyle(s.position === val)}
                  onClick={() => onUpdate({ ...s, position: val })}
                  onKeyDown={(e) => e.key === 'Enter' && onUpdate({ ...s, position: val })}
                >{label}</span>
              ))}
            </div>
          </div>

          {/* Profile link */}
          <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>
            To change your photo, go to{' '}
            <span
              role="button" tabIndex={0}
              onClick={onNavigateProfile}
              onKeyDown={(e) => e.key === 'Enter' && onNavigateProfile()}
              style={{ color: '#7c3aed', cursor: 'pointer', fontWeight: 500 }}
            >Profile Settings</span>
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TEMPLATE GENERATION FUNCTIONS
// ============================================================================

const generateWarmAbout = (answers) => {
  const { drewToWork, whoYouHelp, uniqueApproach, yearsExperience } = answers;

  const experienceText = {
    'less-than-1': 'Beginning this journey',
    '1-3': 'Over the past few years',
    '3-5': 'For several years now',
    '5-10': 'For nearly a decade',
    '10+': 'For over ten years'
  }[yearsExperience] || 'Throughout my practice';

  return `<p>${experienceText}, I've dedicated my practice to helping people find clarity, healing, and direction. ${drewToWork}</p>
<p>I especially love working with ${whoYouHelp}, creating a space where they can feel heard, supported, and truly seen.</p>
<p>${uniqueApproach}</p>
<p>Whether you're seeking answers, healing, or simply a safe space to explore, I'm here to support you on your journey. Every session is approached with compassion, authenticity, and deep respect for your unique path.</p>`;
};

const generateProfessionalAbout = (answers) => {
  const { drewToWork, whoYouHelp, uniqueApproach, yearsExperience } = answers;

  const experienceText = {
    'less-than-1': 'As an emerging practitioner',
    '1-3': 'With several years of dedicated practice',
    '3-5': 'With extensive experience',
    '5-10': 'With nearly a decade of professional experience',
    '10+': 'With over ten years of professional experience'
  }[yearsExperience] || 'As a dedicated practitioner';

  return `<p>${experienceText}, I bring a grounded, professional approach to spiritual guidance. ${drewToWork}</p>
<p>My practice centers on ${whoYouHelp}. I provide clear, actionable insights delivered with integrity and respect for each client's individual journey.</p>
<p>${uniqueApproach}</p>
<p>I'm committed to maintaining the highest standards of professionalism while honoring the sacred nature of this work. Each session is confidential, respectful, and focused on empowering you with the clarity and tools you need.</p>`;
};

const generateSpiritualAbout = (answers) => {
  const { drewToWork, whoYouHelp, uniqueApproach, yearsExperience } = answers;

  const experienceText = {
    'less-than-1': 'Called to this sacred path',
    '1-3': 'Walking this path for a few years now',
    '3-5': 'Deepening in this work over several years',
    '5-10': 'Devoted to this sacred calling for nearly a decade',
    '10+': 'Devoted to this sacred work for over ten years'
  }[yearsExperience] || 'On this sacred journey';

  return `<p>${experienceText}, I hold space for souls seeking light, wisdom, and deeper connection. ${drewToWork}</p>
<p>My heart is drawn to ${whoYouHelp}. In our work together, we create a sacred container where healing unfolds naturally and truth emerges with grace.</p>
<p>${uniqueApproach}</p>
<p>I believe every soul carries divine wisdom within. My role is simply to help you remember, to witness your unfolding, and to honor the sacred mystery of your journey. Together, we'll explore the depths, embrace the light, and trust the wisdom that already lives within you.</p>`;
};

const TONE_GENERATORS = {
  'warm': generateWarmAbout,
  'professional': generateProfessionalAbout,
  'spiritual': generateSpiritualAbout
};

// ============================================================================
// CHARACTER COUNTER COMPONENT
// ============================================================================

const CharacterCounter = ({ count, limit }) => {
  const isAmber = count >= AMBER_THRESHOLD;
  const isRed = count >= limit;

  const color = isRed ? '#DC2626' : isAmber ? '#F59E0B' : '#9CA3AF';

  return (
    <div style={{
      textAlign: 'right',
      fontSize: '13px',
      color,
      fontWeight: isAmber ? 500 : 400,
      marginTop: '8px'
    }}>
      {count} / {limit} characters
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AboutPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('path-selection'); // path-selection, questions, tone, editor
  const [selectedPath, setSelectedPath] = useState(null); // 'help-me-write' or 'write-my-own'
  const [answers, setAnswers] = useState({
    drewToWork: '',
    whoYouHelp: '',
    uniqueApproach: '',
    yearsExperience: ''
  });
  // eslint-disable-next-line no-unused-vars
  const [_selectedTone, setSelectedTone] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  // Load existing about content on mount
  useEffect(() => {
    const existingAbout = state.draft.content.about || '';
    if (existingAbout) {
      setEditorContent(existingAbout);
      setCharacterCount(getPlainTextLength(existingAbout));
    }
  }, []);

  // Helper to get plain text length from HTML
  const getPlainTextLength = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').length;
  };

  // Handle editor content change with character limit
  const handleEditorChange = (html) => {
    const plainText = getPlainTextLength(html);

    if (plainText <= CHARACTER_LIMIT) {
      setEditorContent(html);
      setCharacterCount(plainText);
      actions.updateContent('about', html);
    }
  };

  // Handle path selection
  const handlePathSelect = (path) => {
    setSelectedPath(path);
    if (path === 'write-my-own') {
      setCurrentView('editor');
    } else {
      setCurrentView('questions');
    }
  };

  // Handle question answer
  const handleAnswerChange = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  // Handle tone selection
  const handleToneSelect = (tone) => {
    setSelectedTone(tone);

    const generator = TONE_GENERATORS[tone];
    const generatedHtml = generator(answers);

    setEditorContent(generatedHtml);
    setCharacterCount(getPlainTextLength(generatedHtml));
    actions.updateContent('about', generatedHtml);

    setCurrentView('editor');

    // Show a brief completion indicator so the practitioner knows the content was added
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Check if all questions are answered
  const allQuestionsAnswered = Object.values(answers).every(a => a.trim() !== '');

  // ============================================================================
  // RENDER
  // ============================================================================
  //
  // Section-visibility toggle and PhotoControls are hoisted to the parent return
  // below — they're settings (display preferences for the section/photo), not
  // part of the writing flow, and must be visible regardless of currentView.
  // (B33 fix.)

  let viewBody;

  if (currentView === 'path-selection') {
    viewBody = (
      <>
        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">About You</h3>
          <p className="text-sm text-gray-600">
            Share your journey, what called you to this work, and how you help your clients. Your story is what makes you uniquely you.
          </p>
        </div>

        {/* Inline Callout */}
        <InlineCallout
          text="Your story builds connection before clients even book. Learn how to share your journey authentically."
          playbookTopic="building-your-presence"
        />

        {/* Path Selection Cards */}
        <div className="space-y-4">
          {/* Help Me Write */}
          <button
            onClick={() => handlePathSelect('help-me-write')}
            className="w-full text-left"
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              background: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#7c3aed';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Sparkles size={20} color="white" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    Help Me Write My About
                  </h4>
                  <p className="text-sm text-gray-600">
                    Answer a few questions and we'll create a personalized bio you can customize
                  </p>
                </div>
              </div>
              <ArrowRight size={20} className="text-gray-400 flex-shrink-0" />
            </div>
          </button>

          {/* Write My Own */}
          <button
            onClick={() => handlePathSelect('write-my-own')}
            className="w-full text-left"
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              background: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#7c3aed';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: '#F3F4F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <PenLine size={20} color="#6B7280" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    Write My Own
                  </h4>
                  <p className="text-sm text-gray-600">
                    Start with a blank canvas and write your story from scratch
                  </p>
                </div>
              </div>
              <ArrowRight size={20} className="text-gray-400 flex-shrink-0" />
            </div>
          </button>
        </div>
      </>
    );
  } else if (currentView === 'questions') {
    viewBody = (
      <>
        {/* Header with Back */}
        <div>
          <span
            role="button"
            tabIndex={0}
            onClick={() => setCurrentView('path-selection')}
            onKeyDown={(e) => { if (e.key === 'Enter') setCurrentView('path-selection'); }}
            style={{ fontSize: '13px', color: '#6B7280', cursor: 'pointer', userSelect: 'none', display: 'inline-block', marginBottom: '16px', transition: 'color 0.15s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#374151'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; }}
          >
            Cancel
          </span>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Tell us about you</h3>
          <p className="text-sm text-gray-600">
            Answer these questions to help us craft your About section
          </p>
        </div>

        {/* Questions */}
        <div className="space-y-5">
          {/* Question 1 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What drew you to this work?
            </label>
            <textarea
              value={answers.drewToWork}
              onChange={(e) => handleAnswerChange('drewToWork', e.target.value)}
              placeholder="Share what called you to become a practitioner..."
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Question 2 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who do you love helping most?
            </label>
            <textarea
              value={answers.whoYouHelp}
              onChange={(e) => handleAnswerChange('whoYouHelp', e.target.value)}
              placeholder="Describe your ideal clients or who you're most passionate about serving..."
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Question 3 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What makes your approach unique?
            </label>
            <textarea
              value={answers.uniqueApproach}
              onChange={(e) => handleAnswerChange('uniqueApproach', e.target.value)}
              placeholder="What sets you apart? What do you bring that's distinctly yours?"
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Question 4 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How long have you been practicing?
            </label>
            <select
              value={answers.yearsExperience}
              onChange={(e) => handleAnswerChange('yearsExperience', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
            >
              <option value="">Select experience level</option>
              <option value="less-than-1">Less than 1 year</option>
              <option value="1-3">1-3 years</option>
              <option value="3-5">3-5 years</option>
              <option value="5-10">5-10 years</option>
              <option value="10+">10+ years</option>
            </select>
          </div>
        </div>

        {/* Next Button */}
        <button
          onClick={() => setCurrentView('tone')}
          disabled={!allQuestionsAnswered}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: allQuestionsAnswered ? '#7c3aed' : '#E5E7EB',
            color: allQuestionsAnswered ? 'white' : '#9CA3AF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: allQuestionsAnswered ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease'
          }}
        >
          Next: Choose Your Tone
        </button>
      </>
    );
  } else if (currentView === 'tone') {
    viewBody = (
      <>
        {/* Header with Back */}
        <div>
          <span
            role="button"
            tabIndex={0}
            onClick={() => setCurrentView('questions')}
            onKeyDown={(e) => { if (e.key === 'Enter') setCurrentView('questions'); }}
            style={{ fontSize: '13px', color: '#6B7280', cursor: 'pointer', userSelect: 'none', display: 'inline-block', marginBottom: '16px', transition: 'color 0.15s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#374151'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; }}
          >
            Cancel
          </span>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose your tone</h3>
          <p className="text-sm text-gray-600">
            How do you want your About section to feel?
          </p>
        </div>

        {/* Tone Options */}
        <div className="space-y-3">
          {/* Warm & Personal */}
          <button
            onClick={() => handleToneSelect('warm')}
            className="w-full text-left"
            style={{
              border: '2px solid #E5E7EB',
              borderRadius: '10px',
              padding: '16px',
              background: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#7c3aed';
              e.currentTarget.style.background = '#FAFAFA';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.background = 'white';
            }}
          >
            <div className="font-semibold text-gray-900 text-sm mb-1">Warm & Personal</div>
            <div className="text-xs text-gray-600">
              Friendly, approachable, heartfelt — creates connection through warmth
            </div>
          </button>

          {/* Professional & Confident */}
          <button
            onClick={() => handleToneSelect('professional')}
            className="w-full text-left"
            style={{
              border: '2px solid #E5E7EB',
              borderRadius: '10px',
              padding: '16px',
              background: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#7c3aed';
              e.currentTarget.style.background = '#FAFAFA';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.background = 'white';
            }}
          >
            <div className="font-semibold text-gray-900 text-sm mb-1">Professional & Confident</div>
            <div className="text-xs text-gray-600">
              Clear, grounded, trustworthy — establishes authority and credibility
            </div>
          </button>

          {/* Spiritual & Poetic */}
          <button
            onClick={() => handleToneSelect('spiritual')}
            className="w-full text-left"
            style={{
              border: '2px solid #E5E7EB',
              borderRadius: '10px',
              padding: '16px',
              background: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#7c3aed';
              e.currentTarget.style.background = '#FAFAFA';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.background = 'white';
            }}
          >
            <div className="font-semibold text-gray-900 text-sm mb-1">Spiritual & Poetic</div>
            <div className="text-xs text-gray-600">
              Mystical, evocative, sacred — speaks to the soul and the journey
            </div>
          </button>
        </div>
      </>
    );
  } else {
    // Editor view (default fall-through for currentView === 'editor')
    viewBody = (
      <>
        {/* AI completion banner — shown for 3s after content is generated */}
        {showSuccess && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 14px',
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#15803D',
            fontWeight: 500,
          }}>
            <CheckCircle size={15} color="#16A34A" style={{ flexShrink: 0 }} />
            Added to your About section — feel free to edit below
          </div>
        )}

        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Your About Section</h3>
          <p className="text-sm text-gray-600">
            Write 2-3 paragraphs about your journey, your approach, and who you help. You can use bold, italic, and bullet lists.
          </p>
        </div>

        {/* Editor */}
        <div>
          <SimpleRichEditor
            value={editorContent}
            onChange={handleEditorChange}
            placeholder="Share your story here..."
          />
          <CharacterCounter count={characterCount} limit={CHARACTER_LIMIT} />
        </div>

        {/* Helper Text */}
        <div style={{
          padding: '12px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px'
        }}>
          <p className="text-xs text-gray-600">
            <strong>Tip:</strong> A good About section answers: What drew you to this work? Who do you help? What makes your approach special? Keep it authentic and conversational.
          </p>
        </div>

        {/* Reset Button (if came from template) */}
        {selectedPath === 'help-me-write' && (
          <button
            onClick={() => {
              setCurrentView('path-selection');
              setSelectedPath(null);
              setAnswers({
                drewToWork: '',
                whoYouHelp: '',
                uniqueApproach: '',
                yearsExperience: ''
              });
              setSelectedTone(null);
            }}
            className="text-sm text-gray-600 hover:text-gray-700"
          >
            Start over with a different approach
          </button>
        )}
      </>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Section Visibility — hoisted, always visible regardless of view */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div>
          <p className="text-sm font-medium text-gray-900">Show section on website</p>
          <p className="text-xs text-gray-500 mt-0.5">Toggle off to hide without deleting</p>
        </div>
        <Toggle
          enabled={state.draft.sectionVisibility?.about ?? true}
          onChange={() => actions.toggleSectionVisibility('about')}
          size="small"
        />
      </div>

      {/* Profile Photo Controls — hoisted, always visible regardless of view */}
      <PhotoControls
        photoSettings={state.draft.content.aboutPhoto}
        onUpdate={(settings) => actions.updateContent('aboutPhoto', settings)}
        onNavigateProfile={() => navigate('/dashboard/settings/profile')}
      />

      {viewBody}
    </div>
  );
};

export default AboutPanel;
